import { Autocomplete, TextField } from '@mui/material';
import type { PlanDuration, PlanTrial } from '@weft/types';
import { isNotNullOrUndefined, parseNumber } from '@weft/utils';
import { ArrowIcon } from 'apps/app/src/components/icons/ArrowIcon';
import { ARROW_ICON_DIRECTIONS } from 'apps/app/src/components/icons/ArrowIcon/types/arrow-icon-direction';
import React, { useMemo, useState } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import type { PlanSettingsSelectItem } from './PlanSettingsSelect';

type PlanSettingsType = PlanTrial | PlanDuration;

type PlanCreateAutoCompleteFieldProps = {
  className?: string;
  control: Control;
  name: string;
  values: PlanSettingsSelectItem<PlanSettingsType | undefined>[];
  label: string;
  disabled?: boolean;
  defaultItem: PlanSettingsSelectItem<PlanSettingsType | undefined> | undefined;
  mapValueChange: (inputNumber: number) => PlanSettingsType | undefined;
};

export const PlanSettingsAutoCompleteField: React.FC<PlanCreateAutoCompleteFieldProps> = ({ className, control, mapValueChange, name, label, values, disabled, defaultItem }) => {
  const [customDuration, setCustomDuration] = useState<PlanSettingsSelectItem<PlanSettingsType | undefined>>();

  const options = useMemo(() => getParsedDurationValues(customDuration, values), [customDuration, values]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange: handleChange, ...field } }) => (
        <Autocomplete
          key={defaultItem?.value?.toString()}
          className={twMerge('rounded-2lg w-full cursor-pointer bg-white', className)}
          options={options}
          onClose={() => {
            setCustomDuration(undefined);
          }}
          onChange={(_, value) => {
            handleChange(value.value);
            setCustomDuration(value ?? undefined);
          }}
          defaultValue={defaultItem}
          disabled={disabled}
          isOptionEqualToValue={(option, value) => option.value === value?.value}
          disableClearable
          popupIcon={null}
          componentsProps={{
            paper: {
              elevation: 0,
              className: 'rounded-t-none mt-1',
            },
          }}
          renderOption={(renderProps, option) => (
            <li
              className="flex items-center"
              {...renderProps}
            >
              <span className='w-full text-center text-sm'>{option.label}</span>
            </li>
          )}
          renderInput={(params) => (
            <div className='border-line rounded-2lg relative flex cursor-pointer items-center border'>
              <span className="text-filled-text absolute left-5">{label}</span>
              <TextField
                {...field}
                {...params}
                value={customDuration ? customDuration.value : field.value}
                onChange={(e) => {
                  const input = e.target.value;
                  const inputNumber = parseNumber(input);
                  if (isNotNullOrUndefined(inputNumber) && inputNumber > 0) {
                    const mappedValue = mapValueChange(inputNumber);
                    setCustomDuration({ value: mappedValue, label: `${inputNumber} months` });
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  type: 'search',
                  className: 'text-style text-md font-medium flex pr-5 text-right justify-end cursor-pointer',
                  renderSuffix: () => (
                    <ArrowIcon
                      className="text-style"
                      direction={ARROW_ICON_DIRECTIONS.DOWN}
                    />
                  ),
                  sx: {
                    '.MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& .MuiAutocomplete-input': {
                      textAlign: 'right',
                      maxWidth: '150px',
                      cursor: 'pointer',
                    },
                  },
                }}
              />
            </div>
          )}
        />
      )}
    />
  );
};

function getParsedDurationValues(
  customValue: PlanSettingsSelectItem<PlanSettingsType | undefined> | undefined,
  values: PlanSettingsSelectItem<PlanSettingsType | undefined>[],
) {
  const customAlreadyExist = values.find(({ value }) => value === customValue?.value);

  if (!customValue) {
    return values;
  }

  return !customAlreadyExist ? [customValue, ...values] : values;
}